import {useCallback, useMemo} from 'react';
import {useUserData} from '@/hooks/useUserData';
import {Platform, SOCIAL_PLATFORMS, SocialPlatform} from '@/types/platforms';
import authenticateInstagram from '@/services/instagram/Instagram';
import authenticateTiktok from '@/services/tiktok_creator_market_place/TiktokCreatorMarketPlace';
import authenticateYouTube from '@/services/youtube/Youtube';
import authenticateFacebook from '@/services/facebook/Facebook';
import authenticatePinterest from '@/services/pinterest/Pinterest';

export interface SocialAuthService {
  authenticate: (
    userId: string | null,
    redirectQueryParams?: Record<string, string>
  ) => Promise<void>;
}

// Authentication service mapping
const AUTH_SERVICES = {
  [Platform.Instagram]: {
    authenticate: (userId: string | null, redirectQueryParams = {}) =>
      authenticateInstagram(userId, redirectQueryParams),
  },
  [Platform.TikTok]: {
    authenticate: (userId: string | null, redirectQueryParams = {}) =>
      authenticateTiktok(userId, redirectQueryParams),
  },
  [Platform.YouTube]: {
    authenticate: (userId: string | null, redirectQueryParams = {}) =>
      authenticateYouTube(userId, redirectQueryParams),
  },
  [Platform.Facebook]: {
    authenticate: (userId: string | null, redirectQueryParams = {}) =>
      authenticateFacebook(userId, redirectQueryParams),
  },
  [Platform.Pinterest]: {
    authenticate: (userId: string | null, redirectQueryParams = {}) =>
      authenticatePinterest(userId, redirectQueryParams),
  },
} as const;

const useSocialPlatforms = () => {
  // Get user data and connected platforms
  const {
    user,
    connectedPlatforms,
    isLoading: isUserLoading,
  } = useUserData({
    onConnectedPlatformsChanged: () => {
      // TODO: check if we need to track this
    },
  });

  // Authentication handler
  const authenticatePlatform = useCallback(
    async (
      platform: SocialPlatform,
      redirectQueryParams: Record<string, string> = {}
    ) => {
      const authService = SOCIAL_PLATFORMS.includes(platform)
        ? AUTH_SERVICES[platform]
        : null;
      if (!authService) {
        throw new Error(`Authentication service not found for ${platform}`);
      }

      await authService.authenticate(user?.id || null, redirectQueryParams);
    },
    [user?.id]
  );

  // Get platforms that are not yet connected
  const unconnectedPlatforms = useMemo(
    () =>
      SOCIAL_PLATFORMS.filter(
        (platform) => !connectedPlatforms.includes(platform)
      ),
    [connectedPlatforms]
  );

  // Check if a platform is connected
  const isPlatformConnected = useCallback(
    (platform: SocialPlatform) => connectedPlatforms.includes(platform),
    [connectedPlatforms]
  );

  return {
    // User data
    user,
    connectedPlatforms,
    isUserLoading,

    // Platform lists
    availablePlatforms: SOCIAL_PLATFORMS,
    unconnectedPlatforms,

    // Actions
    authenticatePlatform,
    isPlatformConnected,
  };
};

export default useSocialPlatforms;
